import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  IconButton,
  Button,
} from "@mui/material";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import {
  apiCallSharedFeed,
  apiCallSharedFeedUserList,
} from "../../../../Core/Apicall";

import CommonPagination from "../../../Common/CommonPagination/AdvancedPagination";
import AdvancedPagination from "../../../Common/CommonPagination/AdvancedPagination";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import EmptyData from "../../../Common/EmptyData/EmptyData";

import moment from "moment";
import { useLocation } from "react-router-dom";

const SharedFeedTab: React.FC = () => {
  const [feedList, setFeedList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [receiverTotalItems, setReceiverTotalItems] = useState(0);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / rowsPerPage);


  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [receiverList, setReceiverList] = useState<any[]>([]);
  const [selectedFeedId, setSelectedFeedId] = useState<string>("");

  const [receiverPage, setReceiverPage] = useState(1);
  const [receiverRowsPerPage, setReceiverRowsPerPage] = useState(5);
  const receiverTotalPages = Math.ceil(receiverTotalItems / receiverRowsPerPage);


  const location = useLocation();
  const userData = location.state.user;

  const fetchSharedFeeds = async () => {
    setLoading(true);
    try {
      const payload = {
        userId: userData.userId || userData._id,
        offset: page,
        limit: rowsPerPage,
      };

      const res = await apiCallSharedFeed(payload);

      setFeedList(res.data || []);
      setTotalItems(res.totalCount || 0);
    } catch (error) {
      console.log("Error Fetching Shared Feeds:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiverUsers = async (
    feedId: string,
    page = receiverPage,
    limit = receiverRowsPerPage
  ) => {
    try {
      const payload = {
        userId: userData.userId || userData._id,
        feedId,
        offset: page,
        limit,
      };

      const res = await apiCallSharedFeedUserList(payload);

      setReceiverList(res.data || []);

      setReceiverTotalItems(res.totalCount || 0);
      setReceiverPage(res.offset || page);
      setReceiverRowsPerPage(res.limit || limit);

      setSelectedFeedId(feedId);
      setOpenDialog(true);
    } catch (error) {
      console.log("Error Fetching Receiver Users:", error);
    }
  };

  useEffect(() => {
    if (userData) fetchSharedFeeds();
  }, [page, rowsPerPage]);

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        {/* Loading */}
        {loading && (
          <Box sx={{ p: 2 }}>
            <CommonShimmer />
          </Box>
        )}

        {/* Table */}
        {!loading && (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">ID</TableCell>
                  <TableCell align="center">Feed Media</TableCell>
                  <TableCell align="center">Date</TableCell>
                  <TableCell align="center">Time</TableCell>
                  <TableCell align="center">Total Shared Users</TableCell>
                  <TableCell align="center">User</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {feedList.map((feed, index) => (
                  <TableRow key={feed._id}>
                    {/* ID */}
                    <TableCell align="center">{(page - 1) * rowsPerPage + index + 1}</TableCell>

                    {/* Feed Media */}
                    <TableCell align="center">
                      {feed.feedMedia?.length > 0 ? (
                        <Avatar
                          src={feed.feedMedia[0].thumbnailMediaUrl}
                          variant="rounded"
                          sx={{ width: 55, height: 55 }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{ width: 55, height: 55 }}
                        >
                          N/A
                        </Avatar>
                      )}
                    </TableCell>

                    {/* Date */}
                    <TableCell align="center">
                      {moment(feed.createdAt).format("DD MMM YYYY")}
                    </TableCell>

                    {/* Time */}
                    <TableCell align="center">
                      {moment(feed.createdAt).format("hh:mm A")}
                    </TableCell>

                    {/* Total Shared Users */}
                    <TableCell align="center">
                      <Typography fontWeight="600">
                        {feed.totalSharedUser}
                      </Typography>
                    </TableCell>

                    {/* Action Button */}
                    <TableCell align="center">
                      <IconButton
                        onClick={() => {
                          setReceiverPage(1);
                          setReceiverRowsPerPage(5);
                          fetchReceiverUsers(feed.feedId, 1, 5);
                        }}
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>

                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Empty */}
            {!loading && <EmptyData data={feedList} />}
          </TableContainer>
        )}

        {/* Pagination */}
        <AdvancedPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          onPageChange={(value) => setPage(value)}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
        />

      </Paper>

      {/* Receiver Users Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="md"
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight="600">
            Shared Feed Receiver Users ({receiverTotalItems})
          </Typography>

          <Button onClick={() => setOpenDialog(false)}>✖</Button>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {receiverList.map((user, index) => (
                  <TableRow key={user._id}>
                    {/* ID */}
                    <TableCell>
                      {(receiverPage - 1) * receiverRowsPerPage + index + 1}
                    </TableCell>

                    {/* Profile + Name + Email */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          src={user.profileImagePath}
                          sx={{ width: 45, height: 45 }}
                        />

                        <Box>
                          <Typography fontWeight="600">
                            {user.userName}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {user.userEmailId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Date */}
                    <TableCell>
                      {moment(user.createdAt).format("DD MMM YYYY")}
                    </TableCell>

                    {/* Time */}
                    <TableCell>
                      {moment(user.createdAt).format("hh:mm A")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

            </Table>

            {receiverList.length === 0 && (
              <Typography align="center" sx={{ py: 3 }}>
                No Receiver Users Found
              </Typography>
            )}
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2 }}>
            <AdvancedPagination
              page={receiverPage}
              rowsPerPage={receiverRowsPerPage}
              totalItems={receiverTotalItems}
              onPageChange={(value) => {
                setReceiverPage(value);
                fetchReceiverUsers(selectedFeedId, value, receiverRowsPerPage);
              }}
              onRowsPerPageChange={(value) => {
                setReceiverRowsPerPage(value);
                setReceiverPage(1);
                fetchReceiverUsers(selectedFeedId, 1, value);
              }}
            />

          </Box>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default SharedFeedTab;
